/* eslint-disable sonarjs/cognitive-complexity */
import { BLOCKS } from '@contentful/rich-text-types';
import { twMerge } from 'tailwind-merge';

import { CHANNEL, THEME_CLASSNAMES } from '@boss/constants/corporate-jobs';
import { RichText } from '@boss/rich-text';
import {
  IIVacancy,
  NewsArticleFields,
  SectionFields,
  TabItemFields,
  ThemeColor,
  TimelineEventFields,
} from '@boss/types/corporate-jobs';
import { Carousel, CorporateTabs, Timeline } from '@boss/ui';

import getCorporateTabItemProps from './corporateTabItem';
import getTimelineEventProps from './timeline';
import { CAROUSEL_SETTINGS } from '../../constants/carousel';
import CarouselMapper from '../CarouselMapper';
import ComponentMapper from '../ComponentMapper';

export const getSectionProps = (
  entry: SectionFields,
  locale: string,
  environment: string,
  highlightedJobs?: IIVacancy[] | null,
  mostRecentJobs?: IIVacancy[] | null,
  highlightedNews?: NewsArticleFields[] | null,
  themeColor?: ThemeColor,
  preview?: boolean,
) => {
  const hasBackground = entry.background === 'Apply background (theme color)' && !!themeColor;
  const whiteText = 'text-white';
  const twoColColored = '2-columns-colored';
  const themeColorClassName = themeColor ? THEME_CLASSNAMES.text[themeColor] : '';

  const themeColorBackgroundClassName = themeColor ? THEME_CLASSNAMES.backgroundWithPseudo[themeColor] : '';

  const titleClassName = hasBackground && !!themeColor ? whiteText : themeColorClassName;
  const isColored = entry.displaySectionAs === twoColColored || (hasBackground && !!themeColor);
  const isInColored2ColumnsSectionClassName = isColored ? whiteText : '';

  const title = entry.title ? (
    <RichText
      ComponentMapper={ComponentMapper}
      className={entry.displaySectionAs === 'timeline' ? 'text-center' : ''}
      content={entry.title}
      mapperOptions={{ locale }}
      renderNode={{
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <h2 className={twMerge('mb-4', titleClassName, isInColored2ColumnsSectionClassName)}>{children}</h2>
        ),
      }}
    />
  ) : null;
  const description = entry.description ? (
    <ComponentMapper
      additionalProps={{ background: true }}
      background={hasBackground}
      entry={entry.description}
      environment={environment}
      locale={locale}
      themeColor={themeColor}
    />
  ) : null;

  const getContent = () => {
    // CAROUSEL
    if (entry.displaySectionAs === 'carousel') {
      const mappedEntries = entry.content?.reduce((prev: JSX.Element[], item, index) => {
        // Excludes corrupt entries
        if (!item.__typename) {
          return prev;
        }
        return [...prev, <CarouselMapper entry={item} key={`${item.id}-${index}`} locale={locale} />];
      }, []);

      return (
        <Carousel carouselClassName={'w-full'} options={CAROUSEL_SETTINGS}>
          {mappedEntries}
        </Carousel>
      );
    }

    // TABS & SMALL TABS
    const renderTabs = (variant: 'primary' | 'secondary') => {
      const tabItems: TabItemFields[] = entry.content.filter(
        (item): item is TabItemFields => item.__typename === 'tabItem',
      );
      const mappedItems = tabItems.map(tabItem => getCorporateTabItemProps(tabItem, locale));

      return (
        <CorporateTabs
          buttonClassName={CHANNEL === 'jobs-b2c' ? 'rounded-r-brand rounded-b-brand md:p-4 lg:p-6' : ''}
          contentClassName={hasBackground && !!themeColor ? whiteText : ''}
          items={mappedItems}
          variant={variant}
        />
      );
    };

    if (entry.displaySectionAs === 'tabs') {
      return renderTabs('primary');
    }
    if (entry.displaySectionAs === 'tabs-small') {
      return renderTabs('secondary');
    }

    // TIMELINE
    if (entry.displaySectionAs === 'timeline') {
      // Exclude non-timelineEvents
      const timelineEvents: TimelineEventFields[] = entry.content.filter(
        (fields): fields is TimelineEventFields => fields.__typename === 'timelineEvent',
      );

      const mappedTimelineEvents = timelineEvents.map(event => getTimelineEventProps(event, locale));

      return <Timeline isDev={environment !== 'master' || preview} timelineEvents={mappedTimelineEvents} />;
    }

    // GENERAL
    return (
      <>
        {entry.content?.map((item, index) => (
          <ComponentMapper
            additionalProps={{ parent: entry.__typename, displayAs: entry.displaySectionAs, background: hasBackground }}
            entry={item}
            environment={environment}
            highlightedJobs={highlightedJobs}
            highlightedNews={highlightedNews}
            key={`${entry.id}-${index}`}
            locale={locale}
            mostRecentJobs={mostRecentJobs}
            themeColor={themeColor}
          />
        ))}
      </>
    );
  };

  const cta = entry.cta ? <ComponentMapper entry={entry.cta} environment={environment} locale={locale} /> : null;

  const getBackgroundColorClassName = (): string => {
    if (entry.displaySectionAs === twoColColored) {
      return 'bg-footer-primary';
    }
    if (hasBackground) {
      return themeColorBackgroundClassName;
    }
    return '';
  };

  const getTitleClassName = (): string => {
    if (entry.displaySectionAs === 'timeline') {
      return 'max-w-full';
    }
    if (entry.displaySectionAs === twoColColored || hasBackground) {
      return whiteText;
    }
    return '';
  };

  return {
    title,
    backgroundColor: getBackgroundColorClassName(),
    className: 'my-3 md:my-6',
    description,
    content: getContent(),
    cta,
    ctaPosition: entry.ctaPosition,
    displayAs: entry.displaySectionAs,
    titleClassName: getTitleClassName(),
  };
};

export default getSectionProps;
